'use client';

import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import type { VaultV2BlueReallocation } from '@morpho-org/morpho-sdk';
import { getChainAddresses, type MarketParams, MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon } from '@repo/ui';
import { Alert, Close } from '@repo/ui/icons';
import { useMemo, useState } from 'react';
import { useWalletClient } from 'wagmi';
import type { CheckoutEntryPoint } from '@/lib/analytics/client/events';
import MarketLendWithdrawRecapV2 from '@/components/Bundler/ConfirmDialog/Recaps/MarketLendWithdrawRecapV2';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getRequirementsErrorMessage } from '@/helpers/requirementErrors';
import { isSharedLiquiditySimulationUnverified as getIsSharedLiquiditySimulationUnverified, isRealisedMarketLendAction, submitUnlessDeadlineExpired } from '@/hooks/operation/market/v2/marketAction.helpers';
import { getMarketActionAlertMessage } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketActionBuilder } from '@/hooks/operation/market/v2/useMarketActionBuilder';
import { useMarketSimulatedFinalPosition } from '@/hooks/operation/market/v2/useMarketSimulatedFinalPosition';
import useCloseDialogOnWalletChange from '@/hooks/useCloseDialogOnWalletChange';
import { useReviewConfirmGate } from '@/hooks/useDeferredSubmit';
import { useReviewFunnelTracking } from '@/lib/analytics/client/hooks/useReviewFunnelTracking';
import { buildReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { InterfaceOperation } from '@/operations/operations.types';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';
export interface MarketLendWithdrawReviewDialogV2Props {
  marketParams: MarketParams;
  /** Which side of the lend position this review covers; defaults to the withdraw exit. */
  mode?: 'lend' | 'lendWithdraw';
  /** Loan assets to lend into, or withdraw from, the lend position. */
  amount: bigint;
  /** Native portion wrapped into the wNative loan token inside a lend bundle. */
  nativeAmount?: bigint;
  /** Burn exact supply shares instead of withdrawing a fixed asset amount. */
  shouldUseSupplyShares?: boolean;
  /** Public Allocator reallocations prepended to a shared-liquidity withdraw. */
  reallocations?: readonly VaultV2BlueReallocation[];
  collateralAsset?: InterfaceToken | null;
  loanAsset: InterfaceToken;
  /** APY + rewards source for the recap's APY row (same data as the lend summary card). */
  marketForRewards?: MarketForRewards | null;
  avgSupplyApy?: number | null;
  /** Optional post-success hook; receives the simulated receipt when available. */
  onTxSuccess?: (withdrawAmount: bigint) => void;
  /** Where the review was opened from — drives the checkout-funnel `entry_point` dimension. */
  entryPoint: CheckoutEntryPoint;
}

/**
 * Review + execution for a direct market lend, or a lend-position withdrawal — the
 * lend-side sister of `MarketActionReviewDialogV2`, kept separate because a
 * lender's recap and safety gates differ structurally from a borrower's (no LTV,
 * no close-to-liquidation acknowledgment). Wires
 * `useMarketActionBuilder` (mode `lend` / `lendWithdraw`) →
 * `useMarketSimulatedFinalPosition` (supply leg) → `MarketLendWithdrawRecapV2` →
 * `submitTransactions` + `ConfirmDialogV2`. Reached only behind the market-lend flags —
 * when off the entry points are hidden; there is no legacy fallback.
 */
const MarketLendWithdrawReviewDialogV2 = t0 => {
  const $ = _c(125);
  const {
    marketParams,
    mode: t1,
    amount,
    nativeAmount: t2,
    shouldUseSupplyShares: t3,
    reallocations,
    collateralAsset,
    loanAsset,
    marketForRewards,
    avgSupplyApy,
    onTxSuccess,
    entryPoint
  } = t0;
  const mode = t1 === undefined ? "lendWithdraw" : t1;
  const nativeAmount = t2 === undefined ? ZERO : t2;
  const shouldUseSupplyShares = t3 === undefined ? false : t3;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    data: client
  } = useWalletClient();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    account,
    targetChain,
    targetChainId
  } = useWeb3Context();
  const [reviewSessionId] = useState(_temp);
  useCloseDialogOnWalletChange();
  const isWithdrawMode = mode === "lendWithdraw";
  let t4;
  try {
    let t5;
    if ($[0] !== targetChainId) {
      t5 = getChainAddresses(targetChainId);
      $[0] = targetChainId;
      $[1] = t5;
    } else {
      t5 = $[1];
    }
    t4 = t5.blue;
  } catch {
    t4 = undefined;
  }
  const morphoAddress = t4;
  let t5;
  if ($[2] !== amount || $[3] !== marketParams || $[4] !== mode || $[5] !== nativeAmount || $[6] !== reallocations || $[7] !== shouldUseSupplyShares) {
    t5 = {
      marketParams,
      mode,
      collateralAmount: ZERO,
      loanAmount: amount,
      nativeAmount,
      shouldUseSupplyShares,
      reallocations
    };
    $[2] = amount;
    $[3] = marketParams;
    $[4] = mode;
    $[5] = nativeAmount;
    $[6] = reallocations;
    $[7] = shouldUseSupplyShares;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    transaction,
    requirements,
    areRequirementsReady,
    isRequirementsPlaceholderData,
    kind,
    positionData,
    simulationKey: builtSimulationKey,
    marketError,
    buildError,
    deadline,
    rebuild,
    isRefreshing: isBundleRefreshing,
    isLoading
  } = useMarketActionBuilder(t5);
  const requiresSuccessfulSimulation = isWithdrawMode && !!reallocations?.length;
  let t6;
  if ($[9] !== account.address || $[10] !== requirements || $[11] !== targetChainId) {
    t6 = tryBuildSimulationAuthorizations({
      requirements,
      chainId: targetChainId,
      from: account.address
    });
    $[9] = account.address;
    $[10] = requirements;
    $[11] = targetChainId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = t6;
  const t7 = buildError ?? simulationApprovalError;
  let t8;
  if ($[13] !== kind || $[14] !== reviewSessionId || $[15] !== t7 || $[16] !== targetChainId) {
    t8 = {
      sdkAction: kind,
      chainId: targetChainId,
      reviewSessionId,
      error: t7
    };
    $[13] = kind;
    $[14] = reviewSessionId;
    $[15] = t7;
    $[16] = targetChainId;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  useLogSdkBuildFailed(t8);
  let t9;
  if ($[18] !== positionData) {
    t9 = positionData ? {
      collateral: positionData.collateral,
      borrowAssets: positionData.borrowAssets,
      price: positionData.market.price ?? ZERO,
      supplyAssets: positionData.supplyAssets
    } : undefined;
    $[18] = positionData;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const current = t9;
  let t10;
  if ($[20] !== amount || $[21] !== current || $[22] !== isWithdrawMode || $[23] !== shouldUseSupplyShares) {
    t10 = current ? {
      collateral: current.collateral,
      borrowAssets: current.borrowAssets,
      supplyAssets: isWithdrawMode ? shouldUseSupplyShares ? ZERO : MathLib.zeroFloorSub(current.supplyAssets, amount) : current.supplyAssets + amount
    } : undefined;
    $[20] = amount;
    $[21] = current;
    $[22] = isWithdrawMode;
    $[23] = shouldUseSupplyShares;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const fallback = t10;
  let t11;
  if ($[25] !== builtSimulationKey) {
    t11 = builtSimulationKey ? [builtSimulationKey] : undefined;
    $[25] = builtSimulationKey;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const simulationKey = t11;
  let t12;
  if ($[27] !== areRequirementsReady || $[28] !== current || $[29] !== fallback || $[30] !== isLoading || $[31] !== marketParams || $[32] !== requiresSuccessfulSimulation || $[33] !== simulationAuthorizations || $[34] !== simulationKey || $[35] !== targetChainId || $[36] !== transaction) {
    t12 = {
      chainId: targetChainId,
      marketId: marketParams.id,
      marketParams,
      transaction,
      simulationAuthorizations,
      simulationKey,
      isPreparing: isLoading,
      requiresSuccessfulSimulation,
      areRequirementsReady,
      current,
      fallback
    };
    $[27] = areRequirementsReady;
    $[28] = current;
    $[29] = fallback;
    $[30] = isLoading;
    $[31] = marketParams;
    $[32] = requiresSuccessfulSimulation;
    $[33] = simulationAuthorizations;
    $[34] = simulationKey;
    $[35] = targetChainId;
    $[36] = transaction;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const {
    supplyAfter,
    loanAssetDelta,
    isUnverifiedFinalPosition,
    status: simulationStatus,
    isLoading: isSimLoading,
    isRefreshing: isSimRefreshing
  } = useMarketSimulatedFinalPosition(t12);
  const displayedAmount = isWithdrawMode && shouldUseSupplyShares && loanAssetDelta !== undefined && loanAssetDelta > ZERO ? loanAssetDelta : amount;
  const isSimulationBlacklisted = simulationStatus === "blacklist";
  let t13;
  if ($[38] !== requiresSuccessfulSimulation || $[39] !== simulationStatus) {
    t13 = getIsSharedLiquiditySimulationUnverified({
      hasReallocations: requiresSuccessfulSimulation,
      status: simulationStatus
    });
    $[38] = requiresSuccessfulSimulation;
    $[39] = simulationStatus;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const isSharedLiquiditySimulationUnverified = t13;
  const isSharedLiquiditySimulationUnavailable = requiresSuccessfulSimulation && (simulationStatus === "disabled" || simulationStatus === "error");
  const isHardBlocked = !account.isWalletTypeResolved || !!buildError || !client || account.isWrongChain || !!marketError || isSimulationBlacklisted || simulationApprovalError !== undefined || isSharedLiquiditySimulationUnverified;
  const isPrepared = !!transaction && areRequirementsReady && !isLoading && !isSimLoading;
  const t14 = isWithdrawMode ? "Withdraw" : "Lend";
  const t15 = current?.supplyAssets ?? ZERO;
  const t16 = !current;
  const t17 = isSimLoading || !current;
  let t18;
  if ($[41] !== avgSupplyApy || $[42] !== collateralAsset || $[43] !== displayedAmount || $[44] !== isUnverifiedFinalPosition || $[45] !== loanAsset || $[46] !== marketForRewards || $[47] !== marketParams.id || $[48] !== marketParams.lltv || $[49] !== supplyAfter || $[50] !== t14 || $[51] !== t15 || $[52] !== t16 || $[53] !== t17 || $[54] !== targetChainId) {
    t18 = <MarketLendWithdrawRecapV2 marketId={marketParams.id} chainId={targetChainId} collateralAsset={collateralAsset} loanAsset={loanAsset} amount={displayedAmount} actionLabel={t14} initialSupply={t15} finalSupply={supplyAfter} lltv={marketParams.lltv} marketForRewards={marketForRewards} avgSupplyApy={avgSupplyApy} isInitialPositionLoading={t16} isFinalPositionLoading={t17} isSimulationFallback={isUnverifiedFinalPosition} />;
    $[41] = avgSupplyApy;
    $[42] = collateralAsset;
    $[43] = displayedAmount;
    $[44] = isUnverifiedFinalPosition;
    $[45] = loanAsset;
    $[46] = marketForRewards;
    $[47] = marketParams.id;
    $[48] = marketParams.lltv;
    $[49] = supplyAfter;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = targetChainId;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  const recap = t18;
  const t19 = isWithdrawMode ? InterfaceOperation.InterfaceOperationType.withdraw : InterfaceOperation.InterfaceOperationType.supply;
  let t20;
  if ($[56] !== account.isSafe || $[57] !== entryPoint || $[58] !== requirements || $[59] !== reviewSessionId || $[60] !== t19) {
    t20 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint,
      operationTypes: [t19],
      isSafe: account.isSafe,
      requirements
    });
    $[56] = account.isSafe;
    $[57] = entryPoint;
    $[58] = requirements;
    $[59] = reviewSessionId;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const reviewTrackingParams = t20;
  const {
    trackClose,
    confirmAttempt
  } = useReviewFunnelTracking(reviewTrackingParams, !isLoading);
  let t21;
  if ($[62] !== toggleDialog || $[63] !== trackClose) {
    t21 = () => {
      trackClose();
      toggleDialog(false);
    };
    $[62] = toggleDialog;
    $[63] = trackClose;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  const handleClose = t21;
  let t22;
  if ($[65] !== account || $[66] !== areRequirementsReady || $[67] !== client || $[68] !== confirmAttempt || $[69] !== deadline || $[70] !== displayedAmount || $[71] !== isHardBlocked || $[72] !== isLoading || $[73] !== isSimLoading || $[74] !== isWithdrawMode || $[75] !== kind || $[76] !== loanAsset || $[77] !== marketParams.id || $[78] !== morphoAddress || $[79] !== onTxSuccess || $[80] !== rebuild || $[81] !== recap || $[82] !== requirements || $[83] !== requiresSuccessfulSimulation || $[84] !== submitTransactions || $[85] !== targetChain || $[86] !== toggleDialog || $[87] !== transaction) {
    t22 = () => {
      if (!transaction || !client || !areRequirementsReady || isLoading || isSimLoading || isHardBlocked) {
        return;
      }
      submitUnlessDeadlineExpired({
        deadline,
        rebuild,
        submit: () => {
          const checkoutTracking = confirmAttempt();
          submitTransactions(client, {
            actionType: kind,
            transaction,
            ...getSdkTracking(transaction),
            requirements,
            onTxSuccess: onTxSuccess ? receipt => {
              if (!isRealisedMarketLendAction(receipt?.logs, {
                morphoAddress,
                marketId: marketParams.id,
                userAddress: account.address,
                eventName: isWithdrawMode ? "Withdraw" : "Supply"
              })) {
                return;
              }
              onTxSuccess(displayedAmount);
            } : undefined,
            chain: targetChain,
            account,
            requiresSuccessfulSimulation,
            checkoutTracking
          }).catch(error => {
            logger.error(error instanceof Error ? error : new Error(String(error)), {
              tags: {
                component: "MarketLendWithdrawReviewDialogV2"
              }
            });
            toggleDialog(false);
          });
          toggleDialog(<ConfirmDialogV2 txMessage="Execute your bundle" token={loanAsset} operationContent={recap} />, {
            closable: false
          });
        }
      });
    };
    $[65] = account;
    $[66] = areRequirementsReady;
    $[67] = client;
    $[68] = confirmAttempt;
    $[69] = deadline;
    $[70] = displayedAmount;
    $[71] = isHardBlocked;
    $[72] = isLoading;
    $[73] = isSimLoading;
    $[74] = isWithdrawMode;
    $[75] = kind;
    $[76] = loanAsset;
    $[77] = marketParams.id;
    $[78] = morphoAddress;
    $[79] = onTxSuccess;
    $[80] = rebuild;
    $[81] = recap;
    $[82] = requirements;
    $[83] = requiresSuccessfulSimulation;
    $[84] = submitTransactions;
    $[85] = targetChain;
    $[86] = toggleDialog;
    $[87] = transaction;
    $[88] = t22;
  } else {
    t22 = $[88];
  }
  const handleConfirm = t22;
  const t23 = isRequirementsPlaceholderData || isBundleRefreshing || isSimRefreshing;
  let t24;
  if ($[89] !== handleConfirm || $[90] !== isHardBlocked || $[91] !== isPrepared || $[92] !== t23) {
    t24 = {
      isHardBlocked,
      isPrepared,
      isRefreshSignal: t23,
      submit: handleConfirm
    };
    $[89] = handleConfirm;
    $[90] = isHardBlocked;
    $[91] = isPrepared;
    $[92] = t23;
    $[93] = t24;
  } else {
    t24 = $[93];
  }
  const {
    request: requestConfirm,
    isDisabled: isConfirmDisabled,
    isRefreshing: isConfirmRefreshing
  } = useReviewConfirmGate(t24);
  let t25;
  if ($[94] !== buildError || $[95] !== marketError) {
    t25 = getMarketActionAlertMessage({
      buildError,
      marketError
    });
    $[94] = buildError;
    $[95] = marketError;
    $[96] = t25;
  } else {
    t25 = $[96];
  }
  const alertMessage = t25;
  let t26;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="text-title-sm">Review</span>;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  let t27;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Close />;
    $[98] = t27;
  } else {
    t27 = $[98];
  }
  let t28;
  if ($[99] !== handleClose) {
    t28 = <div className="flex items-center justify-between gap-[16px]">{t26}<Button size="icon" onClick={handleClose} variant="secondary" aria-label="Close">{t27}</Button></div>;
    $[99] = handleClose;
    $[100] = t28;
  } else {
    t28 = $[100];
  }
  let t29;
  if ($[101] !== alertMessage || $[102] !== isLoading) {
    t29 = !isLoading && alertMessage && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{alertMessage}</span></div>;
    $[101] = alertMessage;
    $[102] = isLoading;
    $[103] = t29;
  } else {
    t29 = $[103];
  }
  let t30;
  if ($[104] !== simulationApprovalError) {
    t30 = simulationApprovalError && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{getRequirementsErrorMessage(simulationApprovalError)}</span></div>;
    $[104] = simulationApprovalError;
    $[105] = t30;
  } else {
    t30 = $[105];
  }
  let t31;
  if ($[106] !== isSimulationBlacklisted) {
    t31 = isSimulationBlacklisted && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="market-lend-blacklist-error"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">This transaction can't be completed due to a simulation error.</span></div>;
    $[106] = isSimulationBlacklisted;
    $[107] = t31;
  } else {
    t31 = $[107];
  }
  let t32;
  if ($[108] !== isSharedLiquiditySimulationUnavailable) {
    t32 = isSharedLiquiditySimulationUnavailable && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="market-lend-shared-liquidity-simulation-error"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">Shared liquidity could not be verified. Refresh and try again.</span></div>;
    $[108] = isSharedLiquiditySimulationUnavailable;
    $[109] = t32;
  } else {
    t32 = $[109];
  }
  let t33;
  if ($[110] !== requestConfirm) {
    t33 = () => requestConfirm();
    $[110] = requestConfirm;
    $[111] = t33;
  } else {
    t33 = $[111];
  }
  const t34 = isWithdrawMode ? "Withdraw" : "Lend";
  let t35;
  if ($[112] !== isConfirmDisabled || $[113] !== isConfirmRefreshing || $[114] !== t33 || $[115] !== t34) {
    t35 = <Button data-testid="confirm-button" data-refreshing={isConfirmRefreshing} size="lg" disabled={isConfirmDisabled} onClick={t33}>{t34}</Button>;
    $[112] = isConfirmDisabled;
    $[113] = isConfirmRefreshing;
    $[114] = t33;
    $[115] = t34;
    $[116] = t35;
  } else {
    t35 = $[116];
  }
  let t36;
  if ($[117] !== recap || $[118] !== t28 || $[119] !== t29 || $[120] !== t30 || $[121] !== t31 || $[122] !== t32 || $[123] !== t35) {
    t36 = <div className="gap-lg flex flex-col" data-testid="confirm-dialog">{t28}{recap}{t29}{t30}{t31}{t32}{t35}</div>;
    $[117] = recap;
    $[118] = t28;
    $[119] = t29;
    $[120] = t30;
    $[121] = t31;
    $[122] = t32;
    $[123] = t35;
    $[124] = t36;
  } else {
    t36 = $[124];
  }
  return t36;
};
export default MarketLendWithdrawReviewDialogV2;
function _temp() {
  return crypto.randomUUID();
}